import { logSessionDebug } from './session-debug';

type ComposerBindingsOptions = {
  newChatBtn: HTMLElement;
  newChatArrow: HTMLElement;
  newChatDropdown: HTMLElement;
  importSessionBtn: HTMLElement;
  exportSessionBtn: HTMLElement | null;
  sendBtn: HTMLElement;
  abortBtn: HTMLElement;
  voiceInputBtn: HTMLElement | null;
  quickMessageToggle: HTMLElement | null;
  commandToggle: HTMLElement | null;
  saveQuickMessageBtn: HTMLElement | null;
  modelPickerBtn: HTMLElement | null;
  attachBtn: HTMLElement | null;
  imageUploadInput: HTMLInputElement | null;
  inputWrapper: HTMLElement | null;
  modeSelect: HTMLSelectElement;
  msgInput: HTMLTextAreaElement;
  cmdMenu: HTMLElement;
  quickMessagesController: any;
  voiceInputController: any;
  mobileEnterDebug: any;
  sessionExportController: any;
  sessionState: any;
  composeState: any;
  selectedAgentProvider: () => string;
  isMobileInputMode: () => boolean;
  showNewSessionModal: () => void;
  showImportSessionModal: () => void;
  showImportCodexSessionModal: () => void;
  showModelPicker: () => void;
  sendMessage: () => void;
  sendAbort: () => void;
  autoResize: () => void;
  showCmdMenu: (filter: string) => void;
  hideCmdMenu: () => void;
  navigateCmdMenu: (direction: number) => void;
  selectCmdMenuItem: () => void;
  handleSelectedImageFiles: (files: FileList | File[] | null | undefined) => void;
  setMode: (mode: string) => void;
};

export function bindComposerUi(options: ComposerBindingsOptions) {
  options.newChatBtn.addEventListener('click', () => options.showNewSessionModal());
  options.newChatArrow.addEventListener('click', (e) => {
    e.stopPropagation();
    options.newChatDropdown.hidden = !options.newChatDropdown.hidden;
  });
  options.importSessionBtn.addEventListener('click', () => {
    options.newChatDropdown.hidden = true;
    if (options.selectedAgentProvider() === 'codex') options.showImportCodexSessionModal();
    else options.showImportSessionModal();
  });
  options.exportSessionBtn?.addEventListener('click', () => options.sessionExportController.showSessionExportModal());
  options.sendBtn.addEventListener('click', () => {
    if (options.isMobileInputMode()) options.mobileEnterDebug.notifyWillSend(options.msgInput);
    options.sendMessage();
  });
  options.abortBtn.addEventListener('click', () => {
    logSessionDebug('stop-debug:abort-click', {
      sessionId: options.sessionState.currentSessionId || null,
      isGenerating: !!options.composeState?.isGenerating,
      abortHidden: !!(options.abortBtn as HTMLElement).hidden,
      abortDisabled: !!(options.abortBtn as HTMLButtonElement).disabled,
      sendHidden: !!(options.sendBtn as HTMLElement).hidden,
    });
    options.sendAbort();
    logSessionDebug('stop-debug:abort-sent', {
      sessionId: options.sessionState.currentSessionId || null,
      isGenerating: !!options.composeState?.isGenerating,
    });
  });
  options.voiceInputBtn?.addEventListener('click', () => options.voiceInputController.toggle());
  options.quickMessageToggle?.addEventListener('click', (e) => {
    e.stopPropagation();
    // 工具栏面板互斥展开，避免快捷消息和指令同时占用输入区。
    if (!options.cmdMenu.hidden) options.hideCmdMenu();
    options.quickMessagesController.setPanelOpen(!options.quickMessagesController.isPanelOpen());
  });
  options.commandToggle?.addEventListener('click', (e) => {
    e.stopPropagation();
    // 工具栏面板互斥展开，保持指令菜单与快捷消息的交互一致。
    if (options.quickMessagesController.isPanelOpen()) options.quickMessagesController.setPanelOpen(false);
    if (options.cmdMenu.hidden) options.showCmdMenu('');
    else options.hideCmdMenu();
  });
  options.saveQuickMessageBtn?.addEventListener('click', () => {
    if (options.quickMessagesController.save(options.msgInput.value)) {
      options.quickMessagesController.setPanelOpen(true);
    }
  });
  options.modelPickerBtn?.addEventListener('click', () => options.showModelPicker());

  if (options.attachBtn && options.imageUploadInput) {
    options.attachBtn.addEventListener('click', () => options.imageUploadInput?.click());
    options.imageUploadInput.addEventListener('change', () => {
      options.handleSelectedImageFiles(options.imageUploadInput?.files);
    });
  }

  if (options.inputWrapper) {
    options.inputWrapper.addEventListener('dragover', (e) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      options.inputWrapper?.classList.add('drag-active');
    });
    options.inputWrapper.addEventListener('dragleave', (e) => {
      if (e.target === options.inputWrapper) options.inputWrapper?.classList.remove('drag-active');
    });
    options.inputWrapper.addEventListener('drop', (e) => {
      e.preventDefault();
      options.inputWrapper?.classList.remove('drag-active');
      options.handleSelectedImageFiles(e.dataTransfer?.files);
    });
  }

  options.modeSelect.value = options.sessionState.currentMode;
  document.querySelectorAll('.mode-tab').forEach((tab) => {
    tab.classList.toggle('active', (tab as HTMLElement).dataset.mode === options.sessionState.currentMode);
  });
  document.querySelectorAll('.mode-tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      options.setMode((btn as HTMLElement).dataset.mode || '');
    });
  });
  options.modeSelect.addEventListener('change', () => {
    options.setMode(options.modeSelect.value);
  });

  options.msgInput.addEventListener('input', () => {
    if (options.isMobileInputMode()) options.mobileEnterDebug.logInput(options.msgInput.value);
    options.autoResize();
    options.quickMessagesController.render();
    options.hideCmdMenu();
  });

  options.msgInput.addEventListener('beforeinput', (e) => {
    if (options.isMobileInputMode()) options.mobileEnterDebug.handleBeforeInput(e as InputEvent, options.msgInput.value);
  });

  options.msgInput.addEventListener('keydown', (e) => {
    if (options.isMobileInputMode()) options.mobileEnterDebug.logKeydown(e);
    if (!(options.cmdMenu as HTMLElement).hidden) {
      if (e.key === 'ArrowDown') { e.preventDefault(); options.navigateCmdMenu(1); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); options.navigateCmdMenu(-1); return; }
      if (e.key === 'Tab') { e.preventDefault(); options.selectCmdMenuItem(); return; }
      if (e.key === 'Escape') { options.hideCmdMenu(); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      if (options.isMobileInputMode()) {
        options.mobileEnterDebug.logAction('mobile-enter', {
          action: !(options.cmdMenu as HTMLElement).hidden ? 'allow-enter-with-cmd-menu-open' : 'allow-enter-newline',
          isComposing: !!e.isComposing,
          keyCode: e.keyCode,
        });
        return;
      }

      if (!(options.cmdMenu as HTMLElement).hidden) {
        e.preventDefault();
        options.selectCmdMenuItem();
      } else {
        e.preventDefault();
        options.sendMessage();
      }
    }
  });

  options.msgInput.addEventListener('paste', (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const files = items
      .filter((item) => item.kind === 'file' && /^image\//.test(item.type || ''))
      .map((item) => item.getAsFile())
      .filter(Boolean);
    if (files.length > 0) {
      e.preventDefault();
      options.handleSelectedImageFiles(files as File[]);
    }
  });

  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    if (options.quickMessagesController.isPanelOpen()
      && !target.closest('#quick-message-panel')
      && !target.closest('#quick-message-toggle')) {
      options.quickMessagesController.setPanelOpen(false);
    }
    if (!options.cmdMenu.hidden
      && !target.closest('#cmd-menu')
      && !target.closest('#command-toggle')) {
      options.hideCmdMenu();
    }
  });

  options.quickMessagesController.render();
  options.voiceInputController.updateButton();
}
